import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const loadingMessages = [
  "Identificando planta...",
  "Analisando folhas e estrutura...",
  "Consultando base botânica...",
  "Finalizando identificação..."
];

interface ImageCaptureProps {
  onImageCapture: (base64: string, file: File) => void;
  loading?: boolean;
}

export function ImageCapture({ onImageCapture, loading }: ImageCaptureProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!loading) {
      setMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setMessageIndex(prev => {
        if (prev < loadingMessages.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [loading]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(',')[1];
        setPreview(dataUrl);
        onImageCapture(base64, file);
      };
      reader.readAsDataURL(file);
    }
  }, [onImageCapture]);

  const clearImage = () => {
    setPreview(null);
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
    }
  };

  if (preview) {
    return (
      <div className="relative w-full aspect-[4/3] bg-muted rounded-lg overflow-hidden animate-grow">
        <img
          src={preview}
          alt="Preview"
          className="w-full h-full object-cover"
        />
        {loading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center justify-center text-center">
              <div className="relative w-16 h-16">
                <div className="w-16 h-16 border-4 border-primary/30 rounded-full" />
                <div className="absolute inset-0 w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
              <p 
                key={messageIndex}
                className="mt-4 text-sm text-muted-foreground animate-fade-in"
              >
                {loadingMessages[messageIndex]}
              </p>
            </div>
          </div>
        )}
        {!loading && (
          <Button
            variant="outline"
            size="icon"
            onClick={clearImage}
            className="absolute top-2 right-2 bg-card/80 backdrop-blur"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Input para câmera nativa */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {/* Input para galeria */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <div
        onClick={() => galleryInputRef.current?.click()}
        className={cn(
          "w-full aspect-[4/3] border-2 border-dashed border-border rounded-lg",
          "flex flex-col items-center justify-center gap-4 cursor-pointer",
          "hover:border-primary/50 hover:bg-secondary/50 transition-colors",
          "leaf-pattern"
        )}
      >
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Upload className="h-8 w-8 text-primary" />
        </div>
        <div className="text-center">
          <p className="font-medium text-foreground">Arraste uma imagem ou clique</p>
          <p className="text-sm text-muted-foreground">PNG, JPG até 10MB</p>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => cameraInputRef.current?.click()}
          className="flex-1 touch-target"
        >
          <Camera className="mr-2 h-4 w-4" />
          Usar câmera
        </Button>
        <Button
          variant="outline"
          onClick={() => galleryInputRef.current?.click()}
          className="flex-1 touch-target"
        >
          <Upload className="mr-2 h-4 w-4" />
          Escolher foto
        </Button>
      </div>
    </div>
  );
}
